//@flow
import React, {useState} from 'react';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
  ButtonBase,
  IconButton,
} from '@material-ui/core';

import CreateCollectionModal from '../CreateCollectionModal';
import {useToggle} from '../../../../../../../utils/react';
import InternalPageRouteAutocomplete from '../../../../../../../elements/InternalPageRouteAutocomplete';
import CustomTextField from '../../../../../../../elements/CustomTextField/CustomTextField';
import Tag from '../../../../../../../elements/Tag/Tag';
import {useStoreDomain} from '../../../../../../store/utils';
import {colors} from '../../../../../../../assets/styles/colors';
import {Delete} from '@material-ui/icons';
import {useFormContext} from 'react-hook-form';
import {MAIN_BANNER_FIELDS} from '../constants';

import {getRelatedResourcesService} from '../../../../../../related-resources/promises';

import {
  RELATED_RESOURCE_CRITERIA_LABEL,
  getRelatedResourcesCriteria,
  getRelatedResourcesParams,
} from '../../../../../../related-resources/utils';

import type {
  RelatedResource,
  RelatedResourceParams,
  BannerCollection,
} from '../../../../../../../entities';
import {useBannerCollection} from '../../../../../utils/banner-collection';

export type BannerBehavior =
  | 'NOTHING'
  | 'INNER_ROUTE'
  | 'EXTERNAL_ROUTE'
  | 'CREATE_COLLECTION';

const useStyles = makeStyles(theme => ({
  radioGroupContainer: {
    width: '100%',
  },
  radioGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '1rem',
    flexDirection: 'column',
  },
  collectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '0.5rem',
    background: colors.grey200,
    borderRadius: '.75rem',
    position: 'relative',
  },
  collectionItemsContainer: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    flexWrap: 'wrap',
    borderBottom: `1px solid ${colors.grey500}`,
    paddingBottom: '0.5rem',
    '& span': {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  collectionUrl: {
    padding: '0.75rem',
  },
  collectionActions: {
    display: 'flex',
    gap: '0.25rem',
    position: 'absolute',
    top: 0,
    right: 0,
    padding: '0.25rem',
  },
  collectionActionsButton: {
    background: 'white',

    '&[data-type="delete"]': {
      color: colors.error,
    },
  },
  urlSectionButton: {
    width: '100%',
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    color: colors.grey500,
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '.75rem',
    paddingBlock: '.85rem',
  },
}));

export default function BannerBehaviorSection() {
  const classes = useStyles();

  const {setValue, errors, clearErrors} = useFormContext();

  const {domain} = useStoreDomain();

  const [behaviorSelected, setBehaviorSelected] = useState<BannerBehavior>(
    'INNER_ROUTE'
  );

  const {deleteBannerCollection} = useBannerCollection();

  const [
    collectionItems,
    setCollectionItems,
  ] = useState<RelatedResource | null>(null);

  const [collection, setCollection] = useState<BannerCollection | null>(null);

  const [modalOpen, toggleModalOpen] = useToggle();

  return (
    <div>
      <FormControl component="fieldset" className={classes.radioGroupContainer}>
        <RadioGroup
          aria-label="Tipo de banner"
          value={behaviorSelected}
          className={classes.radioGroup}
          onChange={event => {
            setBehaviorSelected(event.target.value);

            if (event.target.value === 'NOTHING') {
              setValue(MAIN_BANNER_FIELDS.BUTTON_URL_TYPE, 'URL');
              setValue(
                MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER,
                window.location.origin
              );
            } else {
              setValue(MAIN_BANNER_FIELDS.BUTTON_URL_TYPE, '');
              setValue(MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER, '');
            }
          }}
        >
          <FormControlLabel
            value="INNER_ROUTE"
            control={<Radio color="primary" />}
            label="Redirigir a ruta propia"
          />
          {behaviorSelected === 'INNER_ROUTE' && (
            <InternalPageRouteAutocomplete
              onSelectUrl={urlOption => {
                setValue(MAIN_BANNER_FIELDS.BUTTON_URL_TYPE, urlOption.type);
                clearErrors(MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER);

                if (urlOption.type === 'URL') {
                  setValue(
                    MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER,
                    urlOption.url
                  );
                } else {
                  setValue(
                    MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER,
                    urlOption.id
                  );
                }
              }}
              error={!!errors[MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER]}
              helperText={
                errors[MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER]?.message
              }
            />
          )}
          <FormControlLabel
            value="EXTERNAL_ROUTE"
            control={<Radio color="primary" />}
            label="Redirigir a ruta externa"
          />

          {behaviorSelected === 'EXTERNAL_ROUTE' && (
            <CustomTextField
              type="url"
              label="Url de redirección"
              onChange={e => {
                setValue(MAIN_BANNER_FIELDS.BUTTON_URL_TYPE, 'URL');
                setValue(
                  MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER,
                  e.target.value
                );
              }}
              error={!!errors[MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER]}
              helperText={
                errors[MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER]?.message
              }
            />
          )}
          <FormControlLabel
            value="CREATE_COLLECTION"
            control={<Radio color="primary" />}
            label="Crear una colección de productos"
          />

          <FormControlLabel
            value="NOTHING"
            control={<Radio color="primary" />}
            label="No hacer nada"
          />
          {behaviorSelected === 'CREATE_COLLECTION' &&
            !!errors[MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER] && (
              <Typography color="error">
                {errors[MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER]?.message}
              </Typography>
            )}
          {behaviorSelected === 'CREATE_COLLECTION' && !!collectionItems && (
            <div className={classes.collectionContainer}>
              <Typography className={classes.collectionName}>
                {collection?.name}
              </Typography>

              <div className={classes.collectionItemsContainer}>
                <span>
                  {RELATED_RESOURCE_CRITERIA_LABEL[collectionItems.criteria]}:
                </span>
                {collectionItems.resources.map(item => (
                  <Tag flat label={item.name} />
                ))}
              </div>

              <Typography className={classes.collectionUrl}>
                {domain.domainName}/collection/{collection?.id}/
                {collection?.slug}
              </Typography>

              <div className={classes.collectionActions}>
                {/* <IconButton
                  className={classes.collectionActionsButton}
                  data-type="edit"
                >
                  <Edit fontSize="small" />
                </IconButton> */}
                <IconButton
                  className={classes.collectionActionsButton}
                  data-type="delete"
                  onClick={() => {
                    if (!collection) return;
                    deleteBannerCollection(collection.id).then(() => {
                      setCollectionItems(null);
                      setCollection(null);
                    });
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </div>
            </div>
          )}
          {behaviorSelected === 'CREATE_COLLECTION' && !collectionItems && (
            <ButtonBase
              className={classes.urlSectionButton}
              onClick={toggleModalOpen}
            >
              <Typography className={classes.sectionTitle}>
                Crear colección
              </Typography>
            </ButtonBase>
          )}
        </RadioGroup>
      </FormControl>
      <CreateCollectionModal
        onClose={toggleModalOpen}
        open={modalOpen}
        onCreateCollection={collection => {
          setValue(MAIN_BANNER_FIELDS.BUTTON_URL_TYPE, 'COLLECTION');
          setValue(MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER, collection.id);
          clearErrors(MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER);

          const collectionItemCriteria =
            collection.storeReferenceEntities[0].storeReferenceEntityType;
          const ids = collection.storeReferenceEntities.map(
            item => item.collectionReferenceEntityIdentifier
          );
          const params: RelatedResourceParams = getRelatedResourcesParams(
            // $FlowFixMe
            getRelatedResourcesCriteria(collectionItemCriteria),
            ids
          );
          getRelatedResourcesService(params).then(relatedResource => {
            setCollectionItems(relatedResource);
          });
          setCollection(collection);

          toggleModalOpen();
        }}
      />
    </div>
  );
}
